import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bell, Megaphone, Calendar, Building2, Download, FileText, Search, RefreshCw, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const AnnouncementsPage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // data: URL'ler tarayıcıda doğrudan açılamaz/indirilemez; blob'a çevirip aç/indir
  const docToBlobUrl = async (doc) => {
    const res = await fetch(doc.url);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  };

  const handleViewDoc = async (doc) => {
    try {
      const blobUrl = await docToBlobUrl(doc);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error('Belge görüntülenemedi');
    }
  };

  const handleDownloadDoc = async (doc) => {
    try {
      const blobUrl = await docToBlobUrl(doc);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.name || 'belge';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error('Belge indirilemedi');
    }
  };

  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await axios.get(`${backendUrl}/api/announcements/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAnnouncements(response.data);
      
      // Mark all announcements as read
      if (response.data && response.data.length > 0) {
        await axios.post(`${backendUrl}/api/announcements/mark-read`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Duyurular yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Bugün';
    if (diffDays === 1) return 'Dün';
    if (diffDays < 7) return `${diffDays} gün önce`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} hafta önce`;
    return `${Math.floor(diffDays / 30)} ay önce`;
  };

  const filteredAnnouncements = announcements.filter(ann => 
    ann.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ann.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="announcements-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 shadow-xl">
            <Megaphone className="h-7 w-7 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Şirket Bildirimleri
            </h2>
            <p className="text-[hsl(var(--muted-foreground))]">Şirketten gelen duyuru ve bildirimler</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchAnnouncements}
          disabled={loading}
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Yenile
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <Input 
          placeholder="Duyurularda ara..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 py-6 text-lg border-2 focus:border-blue-500 transition-colors"
          data-testid="search-announcements"
        />
      </div>

      {/* Announcements List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500">Duyurular yükleniyor...</p>
          </div>
        </div>
      ) : filteredAnnouncements.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="p-4 rounded-full bg-blue-100 mb-4">
              <Bell className="h-12 w-12 text-blue-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm ? 'Sonuç bulunamadı' : 'Henüz duyuru yok'}
            </h3>
            <p className="text-gray-500 text-center max-w-md">
              {searchTerm 
                ? 'Arama kriterlerinize uygun duyuru bulunamadı.'
                : 'İnsan Kaynakları tarafından paylaşılan duyurular burada görünecek.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredAnnouncements.map((announcement, index) => (
            <Card 
              key={announcement.id} 
              className={`
                overflow-hidden transition-all duration-300 hover:shadow-xl
                border-l-4 border-l-blue-500
                ${index === 0 ? 'ring-2 ring-blue-200 shadow-lg' : ''}
              `}
              data-testid={`announcement-card-${announcement.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {index === 0 && (
                        <Badge className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-0">
                          Yeni
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-blue-600 border-blue-200">
                        {getRelativeTime(announcement.published_date)}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl text-gray-900 leading-tight">
                      {announcement.title}
                    </CardTitle>
                    <CardDescription className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                      <span className="flex items-center gap-1.5 text-blue-600 font-medium">
                        <Building2 className="h-4 w-4" />
                        {announcement.published_by}
                      </span>
                      <span className="flex items-center gap-1.5 text-gray-500">
                        <Calendar className="h-4 w-4" />
                        {formatDate(announcement.published_date)}
                      </span>
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {/* Description */}
                <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-xl p-4 mb-4">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {announcement.description}
                  </p>
                </div>
                
                {/* Documents */}
                {announcement.documents && announcement.documents.length > 0 && (
                  <div className="pt-3 border-t">
                    <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      Ekli Belgeler ({announcement.documents.length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {announcement.documents.map((doc, idx) => (
                        <div
                          key={idx}
                          className="group flex items-center gap-2 px-3 py-2 bg-white border border-blue-200 rounded-lg text-blue-700 shadow-sm"
                          data-testid={`doc-${announcement.id}-${idx}`}
                        >
                          <FileText className="h-4 w-4" />
                          <span className="text-sm font-medium">{doc.name}</span>
                          <button
                            type="button"
                            onClick={() => handleViewDoc(doc)}
                            className="ml-1 p-1 rounded hover:bg-blue-100 transition-colors"
                            title="Görüntüle"
                            data-testid={`view-doc-${announcement.id}-${idx}`}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownloadDoc(doc)}
                            className="p-1 rounded hover:bg-blue-100 transition-colors"
                            title="İndir"
                            data-testid={`download-doc-${announcement.id}-${idx}`}
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Stats Footer */}
      {!loading && filteredAnnouncements.length > 0 && (
        <div className="flex justify-center">
          <p className="text-sm text-gray-500">
            Toplam {filteredAnnouncements.length} duyuru
          </p>
        </div>
      )}
    </div>
  );
};

export default AnnouncementsPage;
